import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";

/**
 * Thin wrapper around `react-signature-canvas` that keeps the same prop contract the rest
 * of the app relies on. We delegate stroke handling, retina scaling and touch routing to
 * the underlying signature_pad library — it's battle-tested across iOS Safari, Android
 * Chrome, stylus + mouse. We layer on top:
 *   • value preload  → paint the user's saved signature into the canvas on mount
 *   • testIds        → consistent data-testids for the testing agent (canvas + clear)
 *   • disabled mode  → lock the canvas to a read-only preview of a saved signature
 *   • onChange       → emits the PNG data URL after every stroke + after clear()
 *
 * Fixes vs upstream signature_pad bug
 *   `undefined is not an object (evaluating 'this._data[this._data.length - 1].push')`
 *   1. `clearOnResize=false` — default resize-clear can null `_data` mid-stroke when the
 *      page scrollbar appears (e.g. modal opens) and crash the next pointermove.
 *   2. Skip the preload effect if `value` is the same dataURL we just emitted — otherwise
 *      parent state echos cause a `clear()+fromDataURL()` cycle that races with the user's
 *      next stroke.
 *   3. Guard every signature_pad call in try/catch + null-checks so a single bad event
 *      doesn't kill the whole React tree.
 *
 * Props:
 *   value          — initial dataURL to show.
 *   onChange       — called with PNG dataURL after every stroke, "" after clear.
 *   onClear        — optional callback fired alongside `onChange("")`.
 *   disabled       — locks the canvas to a static preview of `value`.
 *   width / height — canvas size in CSS pixels.
 *   testId         — base for the data-testids of the canvas + clear button.
 */
export default function SignaturePad({
  value = "",
  onChange,
  onClear,
  disabled = false,
  width = 500,
  height = 180,
  testId = "signature-pad",
}) {
  const padRef = useRef(null);
  const wrapperRef = useRef(null);
  // Tracks the dataURL we last emitted via onChange. If the parent re-renders us with the
  // exact same value (the common case), we DON'T re-run clear()+fromDataURL() — that
  // would risk wiping `_data` mid-stroke and triggering the signature_pad upstream crash.
  const lastEmittedRef = useRef(value || "");
  const [isEmpty, setIsEmpty] = useState(!value);

  // Preload an existing signature image so vendors see their saved stroke when they reopen
  // the page. react-signature-canvas has `fromDataURL` for exactly this.
  useEffect(() => {
    const pad = padRef.current;
    if (!pad) return;
    // Skip if the incoming value matches what we last emitted — it's just the parent
    // echoing our own state back. Running clear()+fromDataURL again would race with the
    // next stroke and is the root cause of the `_data[length-1].push` upstream crash.
    if (value === lastEmittedRef.current) return;
    try { pad.clear(); } catch (e) { /* ignore */ }
    if (value) {
      // Wait one frame so the canvas has its final size before painting the image.
      requestAnimationFrame(() => {
        try {
          pad.fromDataURL(value, { width, height });
          setIsEmpty(false);
        } catch (e) {
          /* malformed dataURL — ignore, leave empty */
        }
      });
    } else {
      setIsEmpty(true);
    }
    lastEmittedRef.current = value || "";
  }, [value, width, height]);

  const handleEnd = () => {
    const pad = padRef.current;
    if (!pad) return;
    try {
      if (pad.isEmpty()) {
        setIsEmpty(true);
        lastEmittedRef.current = "";
        onChange?.("");
        return;
      }
      setIsEmpty(false);
      const dataUrl = pad.toDataURL("image/png");
      lastEmittedRef.current = dataUrl;
      onChange?.(dataUrl);
    } catch (e) {
      // Upstream signature_pad occasionally throws on rapid clear+draw cycles; swallow
      // so we don't kill the page. Worst case the user has to draw the stroke again.
      console.warn("[SignaturePad] onEnd swallowed:", e);
    }
  };

  const clear = () => {
    try { padRef.current?.clear(); } catch (e) { /* ignore */ }
    lastEmittedRef.current = "";
    setIsEmpty(true);
    onChange?.("");
    onClear?.();
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div
        ref={wrapperRef}
        className={`rounded-xl border border-input bg-white inline-block ${
          disabled ? "opacity-80 pointer-events-none" : "hover:border-neutral-400"
        } transition`}
        style={{ width, height }}
        data-testid={`${testId}-canvas`}
      >
        <SignatureCanvas
          ref={padRef}
          penColor="#0f172a"
          minWidth={1.4}
          maxWidth={2.4}
          dotSize={0.8}
          clearOnResize={false}
          backgroundColor="rgba(255,255,255,1)"
          canvasProps={{
            width,
            height,
            className: `block ${disabled ? "cursor-not-allowed" : "cursor-crosshair touch-none"}`,
            style: { width, height, borderRadius: 12 },
          }}
          onEnd={handleEnd}
        />
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-neutral-400">
          {disabled
            ? "Signature locked"
            : isEmpty
              ? "Sign with your finger, stylus or mouse"
              : "Draw again or save below"}
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={clear}
            className="text-neutral-500 hover:text-rose-600 transition font-medium disabled:opacity-40"
            data-testid={`${testId}-clear`}
            disabled={isEmpty}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
